import React, { memo, useMemo } from 'react';

import { useCampaignCardScale, useCampaignDataAttributes, useCampaignState } from 'features/campaigns';
import StringMapper from 'shared/components/StringMapper';
import TransWithFallback from 'shared/components/Translations/TransWrapper';
import { useTrueMobile } from 'shared/hooks/useTrueMobile';
import { useViewMode } from 'store/app/appStore';
import { SINGLE_VIEW_MODE } from '../../../../../../../constants';
import { usePrizeFundFormatting } from 'features/campaigns/hooks/data/usePrizeFundFormatting';
import SpikedTextFrame from '../../../../shared/SpikedTextFrame/SpikedTextFrame';

import rebateVariables from '../../../../../styles/variants/rebate-variables.module.css';
import type { RebateSubtitleCampaignStyles } from './css/base/RebateSubtitleCampaign';
import originalHasManyStyles from './css/hasManyStyles.module.css';
import multiViewStyles from './css/MultiViewRebateSubtitleCampaign.module.css';
import singleViewStyles from './css/SingleViewRebateSubtitleCampaign.module.css';

interface RebateSubtitleCampaignProps {
  content: string | number;
  currency?: string;
  variant?: 'card' | 'panel';
  isSingleItem?: boolean;
}

interface ScaleSettings {
  maxWidth?: number;
  horizontalPadding?: number;
  recalculateKey?: string | number | boolean;
  disabled?: boolean;
  hideUntilFontLoaded?: boolean;

  getContainerWidth?: () => number;
}

const getScaleSettings = ({
  variant,
  hasMany,
  isMobile,
  isCollapsed,
  viewMode,
  isSingleItem,
}: {
  variant?: string;
  hasMany: boolean;
  isMobile: boolean;
  isCollapsed: boolean;
  viewMode?: string;
  isSingleItem?: boolean;
}): ScaleSettings => {
  /* 
    Rebate only uses 'card' (RebateCard) and 'panel' (RebatePanel). Unlike PrizeFundCampaign there is no
    'prize-pool' carousel-topper variant for rebate (no carousel header in the design), so
    no isCarouselVariant handling is needed here.
  */

  if (variant === 'panel') {
    // Panel slogan scales like the campaign title: measure the real container
    // instead of a fixed maxWidth. Panels never collapse; re-measure when the
    // panel switches between single-item and multi-item content.
    return {
      recalculateKey: isSingleItem,
      hideUntilFontLoaded: true,
    };
  }

  if (hasMany) {
    return {
      maxWidth: isMobile ? 210 : isCollapsed ? 300 : 400,
      recalculateKey: isCollapsed,
    };
  }

  return {
    recalculateKey: `${viewMode}-${isCollapsed}`,
    maxWidth: isCollapsed ? (window.innerWidth <= 374 ? 150 : 200) : isMobile ? 250 : undefined,
  };
};

const RebateSubtitleCampaign: React.FC<RebateSubtitleCampaignProps> = ({
  content,
  currency,
  variant,
  isSingleItem,
}) => {
  const { formattedAmount } = usePrizeFundFormatting(content);
  const { isCollapsed, isPortrait } = useCampaignState();
  const viewMode = useViewMode();
  const isMobile = useTrueMobile();
  const dataAttributes = useCampaignDataAttributes({
    additionalProps: { 'data-variant': variant },
  });

  const hasMany = dataAttributes?.['data-has-many']; //no hasMany for Panel
  const hasManyStyles = originalHasManyStyles as unknown as RebateSubtitleCampaignStyles;
  const subtitleCampaignStyles: RebateSubtitleCampaignStyles = (viewMode === SINGLE_VIEW_MODE
    ? singleViewStyles
    : multiViewStyles) as unknown as RebateSubtitleCampaignStyles;

  const isPanel = variant === 'panel';
  const showHasManyClass = !isPanel && hasMany;
  const isCurrencyVerticallyAligned = isCollapsed && (hasMany || (isMobile && isPortrait));

  // Rebate panel (multi view) always shows a translated slogan instead of an amount.
  const displayText = isPanel ? content.toString() : formattedAmount;

  const scaleSettings = useMemo(
    () =>
      getScaleSettings({
        variant,
        hasMany,
        isMobile,
        isCollapsed,
        viewMode,
        isSingleItem,
      }),
    [variant, hasMany, isMobile, isCollapsed, viewMode, isSingleItem],
  );

  const { titleRef, scale, scaledWidth } = useCampaignCardScale(displayText, {
    ...scaleSettings,
  });
  return (
    <div
      className={`${subtitleCampaignStyles.subtitleContainer} ${showHasManyClass ? hasManyStyles.subtitleContainer : ''} ${rebateVariables['rebateTheme']}`}
      {...dataAttributes}
      ref={titleRef as React.RefObject<HTMLDivElement>}
      style={{
        transform: scale < 1 ? `scale(${scale})` : undefined,
        width: scaledWidth ? `${scaledWidth}px` : undefined,
      }}
    >
      <SpikedTextFrame show={!isPanel && !isCollapsed}>
        <div className={subtitleCampaignStyles.subtitleTextWrapper}>
          {isPanel ? (
            <span
              className={`${subtitleCampaignStyles.subtitleTextStyle} ${subtitleCampaignStyles.subtitleTextStyleMain}`}
              data-variant={variant}
            >
              {displayText}
            </span>
          ) : (
            <>
              <span
                className={`${subtitleCampaignStyles.subtitleTextStyle} ${subtitleCampaignStyles.subtitleTextStylePrefix}`}
                data-variant={variant}
              >
                <TransWithFallback i18nKey="rebate.max_win_prefix" />
              </span>
              <span
                className={`${subtitleCampaignStyles.subtitleTextStyle} ${subtitleCampaignStyles.subtitleTextStyleMain}`}
                data-variant={variant}
              >
                {displayText}
              </span>
              {currency && (
                <div
                  className={`${subtitleCampaignStyles.subtitleTextStyle} ${subtitleCampaignStyles.currency} ${showHasManyClass ? hasManyStyles.currency : ''}`}
                  {...dataAttributes}
                >
                  {isCurrencyVerticallyAligned ? (
                    <StringMapper className={subtitleCampaignStyles.subtitleTextStyle} text={currency} />
                  ) : (
                    currency
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </SpikedTextFrame>
    </div>
  );
};

export default memo(RebateSubtitleCampaign);
